import { IconButton } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { ICellRendererParams } from "ag-grid-community";
import ActivityLookupComplete from "components/activity/ActivityDialog/ActivityLookupComplete";
import { ActivityLookupContext } from "components/playbook/context/ActivityLookupContext";
import { useAxios } from "myAxios";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SHARE_FROM_TYPE, SharedFrom } from "types/playbook";
import { getNameOfAConcatString, promiseErrorHandleWithMessage } from "utils";
import ActivityGridEventContext from "../context/ActivityGridEventContext";
import { mutateActivity, mutateTask } from "../utils";
import { AxiosError } from "axios";

const useStyles = makeStyles(() => ({
  info: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
  },
  name: {
    fontSize: 12,
    lineHeight: 1.5,
  },
  role: {
    color: "#898989",
    fontSize: 11,
    lineHeight: 1.5,
  },
}));

const TwoRowsRenderer: FC<Partial<ICellRendererParams> & { from?: SHARE_FROM_TYPE }> = ({
  data,
  column,
  value,
  from = SharedFrom.ACTIVITY,
  node,
}) => {
  const classes = useStyles();
  const { typeOptions, purposeOptions } = useContext(ActivityLookupContext);
  const { control } = useForm();
  const [showForm, setShowForm] = useState(false);
  const [newVal, setNewVal] = useState<string | undefined>("");
  const [oriVal, setOriVal] = useState<string | undefined>("");
  //const oriVal = newVal ? newVal : value;
  const val = value && value !== "N/A" ? ((newVal ? newVal : value) || "").split("%-%") : "N/A";
  const axios = useAxios();

  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);

  const { activityGridEvent } = useContext(ActivityGridEventContext);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setShowForm(false);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  useEffect(() => {
    setOriVal(newVal ? newVal : value);
  }, [newVal, value]);

  const handleChange = async (selected: { value: string; name: string } | null) => {
    setNewVal(selected?.value);
    setShowForm(selected?.value ? false : true);
    // call patch api
    if (selected?.value && selected?.value !== value) {
      try {
        await axios.patch(`/api/dataservice/v1/activities/${data?.id}`, {
          [column?.getColId() as string]: selected?.value,
        });
      } catch (e) {
        const err = e as AxiosError;
        promiseErrorHandleWithMessage(err);
      }

      if (from === SharedFrom.ACTIVITY) {
        mutateActivity(data?.id, activityGridEvent);
      } else {
        mutateTask(undefined, activityGridEvent, data?.id);
      }
    }
  };

  if (!val || !value || !oriVal) {
    return <></>;
  }

  if (node?.rowPinned) return null;

  return (
    <div
      style={{ display: "flex", alignItems: "center" }}
      className="hover-show-container"
      ref={epCellRef}
    >
      {showForm ? (
        column?.getColId() === "type" ? (
          <div
            ref={epPopRef}
            onKeyPress={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
          >
            <Controller
              control={control}
              render={(props) => (
                <ActivityLookupComplete
                  {...props}
                  disabled={!data?.editable || data?.systemRelated}
                  label=""
                  category={"ACTIVITY_TYPE"}
                  showMarginLeft={true}
                  hideTitle={true}
                  handleChange={handleChange}
                  ops={typeOptions}
                />
              )}
              name={"type"}
              defaultValue={
                oriVal
                  ? {
                      name: getNameOfAConcatString(oriVal),
                      value: oriVal,
                      type: val?.[0],
                    }
                  : null
              }
            />
          </div>
        ) : (
          <div
            ref={epPopRef}
            onKeyPress={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
          >
            <Controller
              control={control}
              render={(props) => (
                <ActivityLookupComplete
                  {...props}
                  disabled={!data?.editable || data?.systemRelated}
                  label=""
                  category={"ACTIVITY_PURPOSE"}
                  hideTitle={true}
                  handleChange={handleChange}
                  ops={purposeOptions}
                />
              )}
              name={"purpose"}
              defaultValue={
                oriVal
                  ? {
                      name: getNameOfAConcatString(oriVal),
                      value: `${oriVal}`,
                      type: val?.[0],
                    }
                  : null
              }
            />
          </div>
        )
      ) : val && val !== "N/A" ? (
        <div className={classes.info}>
          <p className={classes.name}>{val?.[0]}</p>
          <p className={classes.role}>{getNameOfAConcatString(oriVal)}</p>
        </div>
      ) : (
        <div>N/A</div>
      )}
      {!data?.editable ||
      data?.systemRelated ||
      (from === SharedFrom.TASK && !data?.taskId) ||
      from === SharedFrom.PLAYBOOK ||
      !((data?.editableFields as string[]) ?? []).includes(column?.getColId() ?? "") ? null : (
        <IconButton
          size="small"
          className={"hover-show"}
          onClick={() => setShowForm(true)}
          style={{ marginLeft: 10, height: "fit-content" }}
        >
          <EditOutlined style={{ color: "#00a0d1", fontSize: "1rem" }} />
        </IconButton>
      )}
    </div>
  );
};

export default TwoRowsRenderer;
